import React, { useEffect, useState } from "react";
// ANT DESIGN COMPONENTS
import { Checkbox, Input, Table, InputNumber, Empty, Tooltip } from "antd";
import { InfoCircleOutlined } from "@ant-design/icons";
import type { CheckboxChangeEvent } from "antd/es/checkbox";
// I18N TRANSLATION
import { useTranslation } from "react-i18next";
// NAVIGATION
import { useLocation } from "react-router-dom";
// REDUX
import { useSelector } from "react-redux";
import { useLazyGetBankQuery, usePutBankMutation } from "../../../redux/banks/bankAPI";
// UTILS
import { getTableRowClass } from "../../../utils/utils";
// AUTHORIZATION
import GETJwtToken from "../../../redux/authentication/authentication";
// COMPONENTS
import BanksFloorConfigurationHeader from "./BanksFloorConfigurationHeader";
import GlobalAlert2 from "../../home/GlobalAlert2";
import TableNoDataBox from "../../components/table/TableNoDataBox";
// INTERFACES
import { BankFloorInterface } from "../../../interfaces/EdgeManagement.interface";
import { LiftInterface } from "../../../interfaces/Lift.interface";
import { BankInterface } from "../../../interfaces/Bank.interface";
import { RootState } from "../../../app/store";
// CONSTANTS
import { BLANK_BANK } from "../../../constants/bank";

type EditableBankFloorField = "name" | "floor_index";
type DoorFieldName = "front_door" | "rear_door";

const hasFloorIndexZero = (bankFloors: BankFloorInterface[] = []) =>
	bankFloors.some((item: BankFloorInterface) => item.floor_index === 0);

function BanksFloorConfigurationTable() {
	// ************************************************ */
	// GLOBAL VARIABLES ******************************* */
	const location = useLocation();
	const bankId = new URLSearchParams(location.search).get("idBank");
	const { Column, ColumnGroup } = Table;
	const [t] = useTranslation("global");

	// ************************************************ */
	// USE STATE VARIABLES **************************** */
	const [bank, setBank] = useState<BankInterface>({ ...BLANK_BANK });
	const [errorMessage, setErrorMessage] = useState<string>("");

	// ************************************************ */
	// REDUX SLICE VARIABLES ************************** */
	const { theme } = useSelector((state: RootState) => state.home);

	// ************************************************ */
	// SERVICES AND API CALLS ************************* */
	const [
		triggerGetBank,
		{ data, isLoading: isLoadingGetBank, isFetching: isFetchingGetBank, isError: isErrorGetBank, error: errorGetBank },
	] = useLazyGetBankQuery();

	const [
		triggerPutBank,
		{
			isSuccess: isSuccessPutBank,
			isError: isErrorPutBank,
			error: errorPutBank,
			isLoading: isLoadingPutBank,
			reset: resetPutBank,
		},
	] = usePutBankMutation();

	// ************************************************ */
	// FUNCTIONS ************************************** */
	const getInitialData = async () => {
		const token = await GETJwtToken();
		triggerGetBank({
			bankId,
			token,
		});
	};

	const onClickSave = async () => {
		const token = await GETJwtToken();
		//
		const copy: BankInterface = structuredClone(bank);
		delete copy.data_blocks;
		copy.lifts = [];
		const BODY = {
			id: bank.id,
			body: {
				...copy,
				lift_floors: bank.lifts,
			},
			token,
		};
		triggerPutBank(BODY);
	};

	const onChangeStartFloorNumber = (newValue: boolean) => {
		const copy: BankInterface = structuredClone(bank);
		copy.start_floor_no = newValue ? 1 : 0;
		setBank(copy);
	};

	const onChangeInputTextValue = (
		newValue: string | number | null,
		bankFloorId: string,
		inputName: EditableBankFloorField
	) => {
		if (bank.bank_floors) {
			const index = bank.bank_floors.findIndex((x: BankFloorInterface) => x.id === bankFloorId);
			if (index >= 0) {
				const copy: BankInterface = structuredClone(bank);
				if (inputName === "name" && typeof newValue === "string") {
					copy.bank_floors[index].name = newValue;
				}
				if (inputName === "floor_index" && typeof newValue === "number") {
					copy.bank_floors[index].floor_index = newValue;
					copy.start_floor_no = hasFloorIndexZero(copy.bank_floors) ? 0 : copy.start_floor_no;
				}
				setBank(copy);
			}
		}
	};

	const onChangeDoorLift = (
		newValue: CheckboxChangeEvent,
		liftId: string,
		bankFloorId: string,
		doorType: "front" | "rear"
	) => {
		const copy: BankInterface = structuredClone(bank);
		if (copy.lifts) {
			const liftIndex = copy.lifts.findIndex((x: LiftInterface) => x.id === liftId);
			if (liftIndex >= 0) {
				if (copy && copy.lifts && copy.lifts[liftIndex] && copy.lifts[liftIndex].lift_floors) {
					const floorIndex = copy.lifts[liftIndex].lift_floors.findIndex((x) => x.bank_floor_id === bankFloorId);
					if (floorIndex >= 0) {
						if (doorType === "front") {
							copy.lifts[liftIndex].lift_floors[floorIndex].front_door = newValue.target.checked;
						} else if (doorType === "rear") {
							copy.lifts[liftIndex].lift_floors[floorIndex].rear_door = newValue.target.checked;
						}
					}
				}
			}
		}
		setBank(copy);
	};

	const onChangeDoorBank = (newValue: CheckboxChangeEvent, bankFloorId: string, doorType: "front" | "rear") => {
		const copy: BankInterface = structuredClone(bank);
		if (copy.bank_floors) {
			const index = copy.bank_floors.findIndex((x: BankFloorInterface) => x.id === bankFloorId);
			if (index >= 0) {
				if (doorType === "front") {
					copy.bank_floors[index].front_door = newValue.target.checked;
				} else {
					copy.bank_floors[index].rear_door = newValue.target.checked;
				}
				setBank(copy);
			}
		}
	};

	const checkInterminateCheckBox = (
		returnValue: "mixed" | "all",
		object: Array<BankFloorInterface | LiftInterface["lift_floors"][number]>,
		valueName: DoorFieldName
	) => {
		// Extracting the values of the property
		const values = object.map((obj) => obj[valueName]);
		// Checking if all values are true
		const allTrue = values.every((value) => value === true);
		// Checking if all values are false
		const allFalse = values.every((value) => value === false);
		// Checking if there are mixed values
		const mixedValues = !allTrue && !allFalse;
		if (returnValue === "mixed") {
			return mixedValues;
		}
		if (allTrue) {
			return true;
		}
		return false;
	};

	const checkAllBoxes = (
		e: CheckboxChangeEvent,
		objectName: "bank_floors" | "lifts",
		valueName: DoorFieldName,
		liftId: string
	) => {
		if (objectName === "bank_floors" && bank.bank_floors) {
			const copy: BankInterface = structuredClone(bank);
			copy.bank_floors.map((_, index: number) => {
				copy.bank_floors[index][valueName] = e.target.checked;
				return true;
			});
			setBank(copy);
		} else if (objectName === "lifts" && bank.lifts) {
			const copy: BankInterface = structuredClone(bank);
			const liftIndex = copy.lifts.findIndex((x: LiftInterface) => x.id === liftId);
			if (liftIndex >= 0) {
				copy.lifts[liftIndex].lift_floors.map((_, index: number) => {
					copy.lifts[liftIndex].lift_floors[index][valueName] = e.target.checked;
					return true;
				});
			}
			setBank(copy);
		}
	};

	const doorCheckedValue = (liftId: string, bankFloorId: string, doorType: string) => {
		if (bank.lifts) {
			const liftIndex = bank.lifts.findIndex((x: LiftInterface) => x.id === liftId);
			if (liftIndex >= 0) {
				const floorIndex = bank.lifts[liftIndex].lift_floors.findIndex((x) => x.bank_floor_id === bankFloorId);
				if (floorIndex >= 0) {
					if (doorType === "front") {
						return bank.lifts[liftIndex].lift_floors[floorIndex].front_door;
					}
					return bank.lifts[liftIndex].lift_floors[floorIndex].rear_door;
				}
			}
		}
		return false;
	};

	const refreshPage = () => {
		getInitialData();
	};

	const getErrorMessageText = (error: unknown): string => {
		if (typeof error !== "object" || error === null || !("data" in error)) {
			return "Error";
		}

		const responseData = (error as { data?: unknown }).data;
		if (typeof responseData !== "object" || responseData === null || !("message" in responseData)) {
			return "Error";
		}

		const message = (responseData as { message?: unknown }).message;
		return typeof message === "string" ? message : "Error";
	};

	// ************************************************ */
	// USE EFFECT ************************************* */
	useEffect(() => {
		getInitialData();
		return () => {
			resetPutBank();
		};
	}, []);

	useEffect(() => {
		if (data && data.data) {
			const sortedByIndex: BankFloorInterface[] = structuredClone(data.data.bank_floors);
			sortedByIndex.sort((a: BankFloorInterface, b: BankFloorInterface) => b.floor_index - a.floor_index);
			const copy: BankInterface = structuredClone(data.data);
			copy.bank_floors = sortedByIndex;
			copy.start_floor_no = hasFloorIndexZero(sortedByIndex) ? 0 : copy.start_floor_no;
			setBank(copy);
		}
	}, [data]);

	useEffect(() => {
		if (errorGetBank) {
			setErrorMessage(getErrorMessageText(errorGetBank));
		}
	}, [errorGetBank]);

	// ************************************************ */
	// COMPONENT ************************************** */
	return (
		<div>
			<div className='mb-2'>
				<BanksFloorConfigurationHeader
					startFloorNumber={bank.start_floor_no === 1}
					disableStartFloorNumberSelection={hasFloorIndexZero(bank.bank_floors || [])}
					bankName={bank.name}
					onClickSave={onClickSave}
					onClickRefreshPage={refreshPage}
					onChangeStartFloorNumber={onChangeStartFloorNumber}
				/>
			</div>
			<Table
				rowClassName={(_record: BankFloorInterface, index: number) => getTableRowClass(index, theme)}
				locale={{
					emptyText: (
						<>
							{isErrorGetBank ? (
								<TableNoDataBox
									isError={isErrorGetBank}
									errorMessage={errorMessage || "Error"}
									noItemsFound='Configuration not found'
								/>
							) : (
								<div className='generalStyles__flexCenter' style={{ height: "300px" }}>
									<Empty description='Configuration not found' />
								</div>
							)}
						</>
					),
				}}
				className='customTable'
				dataSource={bank.bank_floors || []}
				rowKey='id'
				pagination={{
					defaultPageSize: 9999,
					showSizeChanger: false,
				}}
				size='small'
				loading={isLoadingGetBank || isLoadingPutBank || isFetchingGetBank}
			>
				<ColumnGroup>
					{/** POSITION */}
					<Column
						title={
							<div style={{ display: "inline-flex", alignItems: "center", gap: 6 }}>
								<span>Position</span>
								<Tooltip title='This value will be used as the position parameter when sending Hall Call, Car Call, and Action commands.'>
									<InfoCircleOutlined className="generalStyles__info" style={{  cursor: "pointer" }} />
								</Tooltip>
							</div>
						}
						dataIndex='floor_index'
						key='floor_index'
						width='50px'
						render={(text: number) => (
							<InputNumber
								disabled
								placeholder={t("edgeManagement.banks.floorName")}
								value={Math.max(0, bank && bank.start_floor_no === 1 ? text - 1 : text)}
							/>
						)}
					/>
					{/** FLOOR INDEX */}
					<Column
						title='Floor Index'
						dataIndex='floor_index'
						key='floor_index'
						filterSearch
						width='50px'
						render={(text: number, record: BankFloorInterface) => (
							<InputNumber
								placeholder={t("edgeManagement.banks.floorName")}
								value={text}
								onChange={(e: number | null) => {
									onChangeInputTextValue(e, record.id, "floor_index");
								}}
							/>
						)}
					/>
					{/** NAME */}
					<Column
						title={t("general.name")}
						dataIndex='name'
						key='name'
						className='generalStyles__tableHeaderLink'
						filterSearch
						width='50px'
						render={(text: string, record: BankFloorInterface) => (
							<div className='edgeManagement__floorNameName'>
								<Input
									placeholder={t("edgeManagement.banks.floorName")}
									value={text}
									onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
										onChangeInputTextValue(e.target.value, record.id, "name");
									}}
								/>
							</div>
						)}
					/>
				</ColumnGroup>
				{/** BANK FLOORS */}
				<ColumnGroup title='Bank Floors'>
					<Column
						title={
							<div>
								<span className='generalStyles__mrFix'>F</span>
								<span>
									<Checkbox
										indeterminate={checkInterminateCheckBox("mixed", bank.bank_floors || [], "front_door")}
										checked={checkInterminateCheckBox("all", bank.bank_floors || [], "front_door")}
										onChange={(e: CheckboxChangeEvent) => {
											checkAllBoxes(e, "bank_floors", "front_door", "");
										}}
										className='edgeManagement__floorNameTootle'
									/>
								</span>
							</div>
						}
						align='center'
						width={70}
						render={(_text: unknown, record: BankFloorInterface) => (
							<>
								<Checkbox
									className='edgeManagement__floorNameTootle'
									checked={record.front_door}
									onChange={(e: CheckboxChangeEvent) => {
										onChangeDoorBank(e, record.id, "front");
									}}
								/>
							</>
						)}
					/>
					<Column
						title={
							<div>
								<span className='generalStyles__mrFix'>R</span>
								<span>
									<Checkbox
										indeterminate={checkInterminateCheckBox("mixed", bank.bank_floors || [], "rear_door")}
										checked={checkInterminateCheckBox("all", bank.bank_floors || [], "rear_door")}
										onChange={(e: CheckboxChangeEvent) => {
											checkAllBoxes(e, "bank_floors", "rear_door", "");
										}}
										className='edgeManagement__floorNameTootle'
									/>
								</span>
							</div>
						}
						align='center'
						width={70}
						render={(_text: unknown, record: BankFloorInterface) => (
							<>
								<Checkbox
									className='edgeManagement__floorNameTootle'
									checked={record.rear_door}
									onChange={(e: CheckboxChangeEvent) => {
										onChangeDoorBank(e, record.id, "rear");
									}}
								/>
							</>
						)}
					/>
				</ColumnGroup>
				{/** LIFTS */}
				{bank && bank.lifts && (
					<>
						{bank.lifts
							.slice() // Create a shallow copy to avoid mutating the original array
							.sort((a: LiftInterface, b: LiftInterface) => {
								// Compare values based on a specific property (e.g., lift.name)
								if (a.position < b.position) return -1;
								if (a.position > b.position) return 1;
								return 0;
							})
							.map((lift: LiftInterface) => (
								<ColumnGroup title={lift.name} key={lift.id}>
									<Column
										title={
											<div>
												<span className='generalStyles__mrFix'>F</span>
												<span>
													<Checkbox
														indeterminate={checkInterminateCheckBox("mixed", lift.lift_floors, "front_door")}
														checked={checkInterminateCheckBox("all", lift.lift_floors, "front_door")}
														onChange={(e: CheckboxChangeEvent) => {
															checkAllBoxes(e, "lifts", "front_door", lift.id);
														}}
														className='edgeManagement__floorNameTootle'
													/>
												</span>
											</div>
										}
										align='center'
										width={100}
										render={(_text: unknown, record: BankFloorInterface) => (
											<>
												<Checkbox
													className='edgeManagement__floorNameTootle'
													checked={doorCheckedValue(lift.id, record.id, "front")}
													onChange={(e: CheckboxChangeEvent) => {
														onChangeDoorLift(e, lift.id, record.id, "front");
													}}
												/>
											</>
										)}
									/>
									<Column
										title={
											<div>
												<span className='generalStyles__mrFix'>R</span>
												<span>
													<Checkbox
														indeterminate={checkInterminateCheckBox("mixed", lift.lift_floors, "rear_door")}
														checked={checkInterminateCheckBox("all", lift.lift_floors, "rear_door")}
														onChange={(e: CheckboxChangeEvent) => {
															checkAllBoxes(e, "lifts", "rear_door", lift.id);
														}}
														className='edgeManagement__floorNameTootle'
													/>
												</span>
											</div>
										}
										align='center'
										width={100}
										render={(_text: unknown, record: BankFloorInterface) => (
											<>
												<Checkbox
													className='edgeManagement__floorNameTootle'
													checked={doorCheckedValue(lift.id, record.id, "rear")}
													onChange={(e: CheckboxChangeEvent) => {
														onChangeDoorLift(e, lift.id, record.id, "rear");
													}}
												/>
											</>
										)}
									/>
								</ColumnGroup>
							))}
					</>
				)}
			</Table>
			<GlobalAlert2
				isError={isErrorPutBank}
				isSuccess={isSuccessPutBank}
				requestType='PUT'
				error={errorPutBank}
				name='Bank'
			/>
		</div>
	);
}

export default BanksFloorConfigurationTable;
